import React from 'react';
import { useFavouriteUsers } from 'src/queries/loyalty';
import { Box, CircularProgress, LinearProgress, Tooltip, Typography } from '@mui/material';
import {
  FavoriteBorder,
  InfoOutlined,
  PersonAddAlt1Outlined,
  RecyclingOutlined
} from '@mui/icons-material';
import { usePricing } from 'src/hooks/usePricing';
import {
  accent,
  bodyFont,
  caution,
  formatCount,
  headingFont,
  ink,
  positive,
  surface
} from './tokens';

/**
 * The lighter row of context cards under the KPI strip: three figures a
 * merchant cares about that are not really "performance", plus the free-trial
 * meter when it applies.
 */

interface MiniCardProps {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  loading?: boolean;
  children: React.ReactNode;
}

const MiniCard: React.FC<MiniCardProps> = ({ icon, label, hint, loading, children }) => (
  <Box
    sx={{
      backgroundColor: surface.card,
      border: `1px solid ${surface.border}`,
      borderRadius: '16px',
      p: '16px 18px',
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      minWidth: 0,
      height: '100%'
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: '12px',
        backgroundColor: accent.wash,
        color: accent.deep,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none'
      }}
    >
      {icon}
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
        <Typography
          sx={{
            fontFamily: bodyFont,
            fontWeight: 600,
            fontSize: 11,
            letterSpacing: '.09em',
            textTransform: 'uppercase',
            color: ink.faint
          }}
        >
          {label}
        </Typography>
        {hint && (
          <Tooltip title={hint} placement='top' arrow enterTouchDelay={0}>
            <InfoOutlined sx={{ fontSize: 14, color: ink.faint }} />
          </Tooltip>
        )}
      </Box>
      {loading ? (
        <CircularProgress size={18} sx={{ color: accent.mid, my: '4px' }} />
      ) : (
        children
      )}
    </Box>
  </Box>
);

const Figure: React.FC<{ value: string; caption?: string }> = ({ value, caption }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '7px', minWidth: 0 }}>
    <Typography
      sx={{
        fontFamily: headingFont,
        fontWeight: 700,
        fontSize: 24,
        lineHeight: 1.1,
        letterSpacing: '-.01em',
        color: ink.strong
      }}
    >
      {value}
    </Typography>
    {caption && (
      <Typography noWrap sx={{ fontFamily: bodyFont, fontSize: 13, color: ink.muted }}>
        {caption}
      </Typography>
    )}
  </Box>
);

export const NewCustomersCard: React.FC<{ count: number; rangeLabel: string }> = ({
  count,
  rangeLabel
}) => (
  <MiniCard
    icon={<PersonAddAlt1Outlined sx={{ fontSize: 21 }} />}
    label='New customers'
    hint='Customers whose very first stamp at this location falls inside the selected period.'
  >
    <Figure value={formatCount(count)} caption={`in ${rangeLabel}`} />
  </MiniCard>
);

/**
 * Customers holding this location's card in their wallet. Its own request —
 * `loyalty/favourite-users/:id` is not part of the stats payload.
 */
export const WalletSavesCard: React.FC<{ merchantId: number }> = ({ merchantId }) => {
  // Shares a cache entry with the digital-flyers page, which sizes a campaign
  // from the same number — so the two pages make one request between them.
  const { data, isFetching: loading, isError: failed } = useFavouriteUsers(merchantId);
  const count = failed ? null : data?.length ?? null;

  return (
    <MiniCard
      icon={<FavoriteBorder sx={{ fontSize: 20 }} />}
      label='Wallet saves'
      hint='Customers who have pinned this location’s card in their app, so it is one tap from their home screen.'
      loading={loading}
    >
      {failed ? (
        <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.faint }}>
          Unavailable
        </Typography>
      ) : (
        <Figure value={formatCount(count ?? 0)} caption='customers' />
      )}
    </MiniCard>
  );
};

/**
 * Printed loyalty cards the programme has replaced — one per customer, plus one
 * per completed card — priced in the merchant's own currency.
 */
const BASE_CARD_VALUE_ZAR = 5.5;

export const PaperCardsSavedCard: React.FC<{
  uniqueCustomers: number;
  rewardsIssued: number;
  countryCode?: string;
}> = ({ uniqueCustomers, rewardsIssued, countryCode }) => {
  const { pricing, loading } = usePricing(countryCode);

  const zarPerUnit = pricing?.zar_per_unit ?? null;
  // Without a conversion rate the honest fallback is the base figure in its
  // original currency, rather than a local-looking number that is wrong.
  const symbol = pricing && zarPerUnit ? pricing.symbol : 'R';
  const perCard = zarPerUnit ? BASE_CARD_VALUE_ZAR / zarPerUnit : BASE_CARD_VALUE_ZAR;

  const cards = uniqueCustomers + rewardsIssued;
  const savings = cards * perCard;

  return (
    <MiniCard
      icon={<RecyclingOutlined sx={{ fontSize: 21 }} />}
      label='Paper cards saved'
      hint={`One per customer, plus one for every completed card. Valued at ${symbol}${perCard.toFixed(2)} each.`}
      loading={loading}
    >
      <Figure
        value={formatCount(cards)}
        caption={`≈ ${symbol}${savings.toFixed(2)} saved`}
      />
    </MiniCard>
  );
};

/** Trials end at whichever comes first: 500 stamps or two months. */
const FREE_TRIAL_STAMPS = 500;

export const FreeTrialProgress: React.FC<{ stampsAllTime: number }> = ({ stampsAllTime }) => {
  const used = Math.min(stampsAllTime, FREE_TRIAL_STAMPS);
  const remaining = Math.max(FREE_TRIAL_STAMPS - stampsAllTime, 0);
  const percent = (used / FREE_TRIAL_STAMPS) * 100;
  const runningLow = remaining <= 100;

  return (
    <Box
      sx={{
        backgroundColor: surface.card,
        border: `1px solid ${surface.border}`,
        borderRadius: '16px',
        p: '18px 20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '12px',
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography
            sx={{
              fontFamily: headingFont,
              fontWeight: 700,
              fontSize: 17,
              color: ink.strong
            }}
          >
            Free trial
          </Typography>
          <Typography sx={{ fontFamily: bodyFont, fontSize: 14, color: ink.muted }}>
            {remaining > 0
              ? `${formatCount(remaining)} stamps left, or two months from sign-up — whichever comes first.`
              : 'You have used all 500 trial stamps. Start a subscription to keep scanning.'}
          </Typography>
        </Box>
        <Typography
          sx={{
            fontFamily: bodyFont,
            fontWeight: 600,
            fontSize: 14,
            color: runningLow ? caution.fg : ink.body
          }}
        >
          {`${formatCount(used)} / ${FREE_TRIAL_STAMPS}`}
        </Typography>
      </Box>
      <LinearProgress
        variant='determinate'
        value={percent}
        aria-label='Free trial stamps used'
        sx={{
          height: 8,
          borderRadius: 99,
          backgroundColor: surface.grid,
          '& .MuiLinearProgress-bar': {
            borderRadius: 99,
            backgroundColor: runningLow ? caution.fg : positive.fg
          }
        }}
      />
    </Box>
  );
};
